import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ClaimApiBaseService } from '@core/api/claim-api.service';
import { CrudApiBaseService } from '@core/api/crud-api.base';
import { Claim } from '@core/interfaces/claims/claim.interface';
import { ClaimDetails, FileInterface, IClaimNote } from '@core/interfaces/claims/claimDetails.interface';
import { Files } from '@core/interfaces/claims/files.interface';
import { Theme } from '@core/interfaces/ui/theme.interface';
import { ApplicationInsightsService } from '@core/services/application-insights/application-insights.service';
import { FileService } from '@core/services/file.service';
import { collectUploadEvidence } from '@core/utils/upload-evidence.util';
import { NotificationType } from '@shared/modules/notification/enums/notification-type.enum';
import { NotificationService } from '@shared/modules/notification/services/notification.service';

@Injectable({
  providedIn: 'root',
})
export class MyClaimApiService extends CrudApiBaseService<Claim> implements ClaimApiBaseService {
  endpoint = 'myclaim';

  constructor(
    private notification: NotificationService,
    private readonly fileService: FileService,
    private readonly applicationInsightsService: ApplicationInsightsService,
  ) {
    super();
  }

  getClaimDetails(id: number): Observable<ClaimDetails> {
    return this.httpClient.get<ClaimDetails>(this.getUrl(`${id}`));
  }

  getFiles(id: number) {
    return this.fileService.getFileViewerWidth().pipe(
      switchMap(width =>
        this.httpClient
          .get<Files[]>(this.getUrl(`${id}/files`), {
          params: {
            width,
          },
        })
          .pipe(map(files => this.fileService.sortFiles(files))),
      ),
    );
  }

  getCommunicationAttachments(claimId: number, activityId: string) {
    return this.httpClient.get<FileInterface[]>(this.getUrl(`${claimId}/attachments/${activityId}`));
  }

  createClaim(data: any, files: Files[]) {
    const model = JSON.stringify(data);
    const startedAt = Date.now();

    return from(collectUploadEvidence(files)).pipe(
      switchMap(({ evidence, unreadableFileNames }) => {
        this.applicationInsightsService.logEvent('ConsumerRegisterClaim', {
          body: data,
          modelLength: model.length,
          ...evidence,
        });

        if (unreadableFileNames.length > 0) {
          this.applicationInsightsService.logEvent('ConsumerRegisterClaimBlocked', {
            reason: 'unreadable-files',
            ...evidence,
          });
          this.notification.next({
            message:
              'Some of your photos can no longer be read from your device: '
              + `${unreadableFileNames.join(', ')}. `
              + 'Please remove and re-attach them, then submit your claim again.',
            type: NotificationType.Error,
            duration: 10000,
          });
          return throwError(() => new Error('Claim submission blocked: some files are no longer readable'));
        }

        const formData: FormData = new FormData();
        // model goes first so a truncated request body loses photos, not the claim payload
        formData.append('model', model);
        for (let i = 0; i < files.length; i++) {
          formData.append('formfiles', files[i].originalFile);
        }

        return this.httpClient.post<boolean>(this.getUrl(''), formData).pipe(
          tap(() => {
            this.applicationInsightsService.logEvent('ConsumerRegisterClaimSucceeded', {
              elapsedMs: Date.now() - startedAt,
              ...evidence,
            });
            this.notification.next({
              message:
                'You claim has been submitted for processing. '
                + 'You will receive the Claim Number shortly, as soon as it has been assigned.',
              type: NotificationType.Success,
              duration: 6000,
            });
          }),
          catchError((error: unknown) => {
            const httpError = error as HttpErrorResponse;
            this.applicationInsightsService.logEvent('ConsumerRegisterClaimFailed', {
              status: httpError?.status ?? -1,
              statusText: httpError?.statusText ?? '',
              serverMessage: `${JSON.stringify(httpError?.error ?? '')}`.slice(0, 256),
              elapsedMs: Date.now() - startedAt,
              modelLength: model.length,
              ...evidence,
            });
            return throwError(() => error);
          }),
        );
      }),
    );
  }

  saveNote(id: number, note: string): Observable<IClaimNote> {
    return this.httpClient.post<IClaimNote>(this.getUrl(`${id}/note`), {
      note,
    });
  }

  tenantInfo(encryptedClaimPortalToken: string) {
    return this.httpClient.get<ClaimDetails & {tenantInfo: Theme}>(this.getUrl('claimTenantInfo'), {
      params: {
        encryptedClaimPortalToken,
      },
    });
  }
}
